// ═══════════════════════════════════════════════════════
//  Toast Notification Utility
//
//  Singleton container at #toast-stack. Multiple toasts
//  stack vertically. Auto-removes after `duration` ms.
//  Accessibility: role=status, aria-live=polite.
// ═══════════════════════════════════════════════════════

const DEFAULTS = {
	duration: 2000,
	type: 'default',     // 'default' | 'success' | 'error'
	position: 'bottom',  // 'bottom' | 'top'
};

let _container = null;
let _idCounter = 0;

function ensureContainer(position) {
	if (_container && _container.dataset.position === position) return _container;
	if (_container) _container.remove();
	_container = document.createElement('div');
	_container.id = 'toast-stack';
	_container.dataset.position = position;
	_container.setAttribute('role', 'region');
	_container.setAttribute('aria-label', 'Notifications');
	document.body.appendChild(_container);
	return _container;
}

export function showToast(message, options) {
	if (!message) return;
	const opts = Object.assign({}, DEFAULTS, options || {});
	const container = ensureContainer(opts.position);
	const id = `toast-${++_idCounter}`;
	const toast = document.createElement('div');
	toast.id = id;
	toast.className = `toast -${opts.type}`;
	toast.setAttribute('role', 'status');
	toast.setAttribute('aria-live', 'polite');
	toast.setAttribute('aria-atomic', 'true');

	// Icon prefix per type
	const icon = opts.type === 'success' ? '✓'
		: opts.type === 'error'   ? '!'
		: '';
	const iconEl = document.createElement('span');
	iconEl.className = 'toast-icon';
	iconEl.setAttribute('aria-hidden', 'true');
	iconEl.textContent = icon;
	const msgEl = document.createElement('span');
	msgEl.className = 'toast-message';
	msgEl.textContent = message;
	toast.appendChild(iconEl);
	toast.appendChild(msgEl);
	container.appendChild(toast);

	// Trigger enter animation
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			toast.classList.add('-visible');
		});
	});

	setTimeout(() => {
		toast.classList.remove('-visible');
		setTimeout(() => {
			if (toast.parentNode) toast.remove();
			if (container && !container.firstChild) {
				container.remove();
				_container = null;
			}
		}, 240);
	}, opts.duration);
}

// Convenience helpers
export const toast = {
	success: (msg, opts) => showToast(msg, Object.assign({}, opts, { type: 'success' })),
	error:   (msg, opts) => showToast(msg, Object.assign({}, opts, { type: 'error'   })),
	info:    (msg, opts) => showToast(msg, Object.assign({}, opts, { type: 'default' })),
};

export function clearToasts() {
	if (_container) {
		_container.remove();
		_container = null;
	}
}
